import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Instagram, Menu, X, Info, Building2 } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const RED = "#C41230";
const BLACK = "#161113";
const NAME = "St. Michael's Dental Care";
const FB = "https://www.facebook.com/saintmichaelsdentalcare/";
const IG = "https://www.instagram.com/st.michaelsdentalcare/";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function StMichaelsConcept() {
  const [open, setOpen] = useState(false);
  const links = [["About", "#about"], ["Services", "#services"], ["Branches", "#branches"], ["Reviews", "#reviews"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen bg-white" style={{ color: "#1A1517", fontFamily: "'Barlow', sans-serif" }} data-testid="stmichaels-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@500;600;700&family=Barlow:wght@400;500;600;700&display=swap'); .sm2-oswald{font-family:'Oswald',sans-serif;}`}</style>

      <div data-testid="dental-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2"><Info size={13} className="text-white/70" /> Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white border-b-2" style={{ borderColor: RED }}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="sm2-logo" className="flex items-center gap-3">
            <img src="/dental/st-michaels-dental-concept/logo.jpg" alt={NAME} className="h-14 object-contain" />
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="sm2-oswald text-sm uppercase tracking-wide text-black/70 hover:text-black transition-colors">{l}</a>)}
          </div>
          <a href="#visit" data-testid="sm2-header-cta" className="hidden sm:inline-flex px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{ background: RED }}>Book a Visit</a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="sm2-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — bold crest / heraldic identity */}
      <section className="relative py-24 sm:py-28 text-center overflow-hidden" style={{ background: BLACK }} data-testid="sm2-hero">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px)", backgroundSize: "26px 26px" }} />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp}>
            <span className="sm2-oswald text-xs uppercase tracking-[0.3em]" style={{ color: RED }}>Iloilo City · Est. 2016</span>
            <h1 className="mt-6 sm2-oswald text-5xl sm:text-6xl lg:text-7xl text-white uppercase tracking-tight">Miles of Smiles</h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl mx-auto">Oral health, orthodontics, implants and restorative care from two locations across Iloilo City.</p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <a href="#visit" data-testid="sm2-hero-book" className="px-8 py-3.5 text-sm font-semibold text-white" style={{ background: RED }}>Book a Visit</a>
              <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="sm2-hero-message" className="px-8 py-3.5 text-sm font-semibold text-white border border-white/30">Message Us</a>
            </div>
            <p className="mt-8 text-sm text-white/75">4.7★ Google · 62 Reviews &nbsp;·&nbsp; 4.9★ WhatClinic · 4 Verified Reviews</p>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <motion.p {...fadeUp} className="text-2xl sm:text-3xl leading-relaxed" style={{ fontFamily: "'Oswald', sans-serif" }}>
          St. Michael's Dental Care has served Iloilo City since 2016, offering oral health consultation through to implants and orthodontics.
        </motion.p>
      </section>

      {/* SERVICES — the clinic's own full services-menu graphic */}
      <section id="services" className="py-20" style={{ background: "#FBEBEC" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="sm2-oswald text-3xl uppercase text-center mb-10" style={{ color: RED }}>Our Services</motion.h2>
          <motion.img {...fadeUp} src="/dental/st-michaels-dental-concept/services-banner.jpg" alt="Dental needs and services offered at St. Michael's Dental Care" className="w-full object-contain shadow-md" />
        </div>
      </section>

      {/* RESULT */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <motion.h2 {...fadeUp} className="sm2-oswald text-3xl uppercase mb-10">Tooth Restoration</motion.h2>
        <motion.img {...fadeUp} src="/dental/st-michaels-dental-concept/before-after.jpg" alt="A tooth restoration case, before and after, shared by St. Michael's Dental Care" className="w-full max-w-lg mx-auto object-contain shadow-lg" />
      </section>

      {/* BRANCHES */}
      <section id="branches" className="py-20" style={{ background: BLACK }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="sm2-oswald text-3xl uppercase text-white text-center mb-12">Two Locations in Iloilo</motion.h2>
          <div className="grid sm:grid-cols-2 gap-8">
            {[
              { name: "Main Branch", note: "1/F EROS Pension, Gen. Luna Street, Iloilo City", phone: "(033) 320-0289 / 0947 777 5588" },
              { name: "Molo Branch", note: "Rm 6 Doña Maria Building, corner San Juan & Avanceña St., Molo, Iloilo City", phone: "(033) 351-1127" },
            ].map((b) => (
              <motion.div key={b.name} {...fadeUp} data-testid="sm2-branch" className="p-8 border border-white/15">
                <Building2 size={22} style={{ color: RED }} />
                <h3 className="mt-4 sm2-oswald text-xl uppercase text-white">{b.name}</h3>
                <p className="mt-2 text-white/60">{b.note}</p>
                <p className="mt-2 text-white/60">{b.phone}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="reviews" className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
        <motion.h2 {...fadeUp} className="sm2-oswald text-3xl uppercase text-center mb-12" style={{ color: RED }}>What Patients Say</motion.h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { text: "Personnel were professional and the overall dental experience was very good.", author: "Dave Ramonette Trimanez", source: "Google review" },
            { text: "Staff are extremely accommodating and caring — they even check on you after the appointment.", author: "Gene", source: "Google review" },
            { text: "Staff are very nice, patient and relaxed during treatment — by far the best dental practice.", source: "WhatClinic" },
          ].map((q, i) => (
            <motion.div key={i} {...fadeUp} data-testid={`sm2-testimonial-${i}`} className="p-7 border-t-2" style={{ borderColor: RED }}>
              <p className="text-[#332A2C]/85 leading-relaxed">"{q.text}"</p>
              <p className="mt-4 text-sm font-semibold" style={{ color: RED }}>{q.author ? `${q.author} · ` : ""}{q.source}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="py-20" style={{ background: "#FBEBEC" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="sm2-oswald text-3xl uppercase" style={{ color: RED }}>Book an Appointment</h2>
            <ul className="mt-8 space-y-5">
              <li className="flex items-start gap-4"><MapPin size={20} style={{ color: RED }} className="mt-0.5" />1/F EROS Pension, Gen. Luna Ave., Iloilo City Proper</li>
              <li className="flex items-start gap-4"><Phone size={20} style={{ color: RED }} className="mt-0.5" /><a href="tel:+639477775588" className="hover:underline">+63 947 777 5588</a></li>
              <li className="flex items-start gap-4"><Mail size={20} style={{ color: RED }} className="mt-0.5" /><a href="mailto:st.michaelsdentalcare@hotmail.com" className="hover:underline">st.michaelsdentalcare@hotmail.com</a></li>
              <li className="flex items-start gap-4"><Facebook size={20} style={{ color: RED }} className="mt-0.5" /><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="sm2-facebook-link" className="hover:underline">saintmichaelsdentalcare</a></li>
              <li className="flex items-start gap-4"><Instagram size={20} style={{ color: RED }} className="mt-0.5" /><a href={IG} target="_blank" rel="noopener noreferrer" data-testid="sm2-instagram-link" className="hover:underline">st.michaelsdentalcare</a></li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="min-h-[320px] shadow-md">
            <iframe title={`${NAME} map`} src={`https://www.google.com/maps?q=${encodeURIComponent("Gen Luna Ave Iloilo City Proper")}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </motion.div>
        </div>
      </section>

      <footer className="py-10 bg-white border-t-2" style={{ borderColor: RED }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-black/50">
          <p className="sm2-oswald text-base uppercase" style={{ color: RED }}>St. Michael's Dental Care</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {NAME}. Confirm details directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact" className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl" style={{ background: RED }}>Message St. Michael's</a>
      <ConceptCTAPill slug="st-michaels-dental-concept" />
    </div>
  );
}
